/**
 * Money is always an integer number of minor units (satang for THB) plus a
 * currency code. Floating point is never used for stored amounts.
 */
export type CurrencyCode = 'THB';
export const DEFAULT_CURRENCY: CurrencyCode = 'THB';

/** Largest amount we accept in minor units (fits safely in a JS number and PG bigint). */
export const MAX_MINOR = 1_000_000_000_00; // 1,000 million baht

export class MoneyParseError extends Error {
  override name = 'MoneyParseError';
}

/**
 * Parse a human-entered baht amount ("1,500", "1500.5", "฿ 1,500.50") into satang.
 * String-based so that 0.1 + 0.2 style float errors can never happen.
 */
export function bahtToMinor(input: string | number): number {
  const raw = typeof input === 'number' ? numberToPlainString(input) : input;
  const cleaned = raw.replace(/[฿,\s]/g, '').replace(/บาท$/u, '');
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) throw new MoneyParseError(`จำนวนเงินไม่ถูกต้อง: "${raw}"`);
  const [, sign, whole, frac = ''] = match;
  const minor = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(minor) || minor > MAX_MINOR) {
    throw new MoneyParseError(`จำนวนเงินเกินขอบเขต: "${raw}"`);
  }
  return sign ? -minor : minor;
}

function numberToPlainString(n: number): string {
  if (!Number.isFinite(n)) throw new MoneyParseError('จำนวนเงินไม่ถูกต้อง');
  // toFixed(2) is exact enough for values that were typed with <= 2 decimals.
  const fixed = n.toFixed(2);
  if (Math.abs(Number(fixed) - n) > 1e-9) {
    throw new MoneyParseError('จำนวนเงินมีทศนิยมเกิน 2 ตำแหน่ง');
  }
  return fixed;
}

/** Whole baht (integers from legacy systems) to satang. */
export function wholeBahtToMinor(baht: number): number {
  if (!Number.isInteger(baht)) throw new MoneyParseError('ต้องเป็นจำนวนเต็มบาท');
  return baht * 100;
}

export function formatMinor(minor: number, currency: CurrencyCode = DEFAULT_CURRENCY, opts: { symbol?: boolean } = {}): string {
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.trunc(abs / 100);
  const frac = abs % 100;
  const wholeStr = whole.toLocaleString('en-US');
  const body = frac === 0 ? wholeStr : `${wholeStr}.${String(frac).padStart(2, '0')}`;
  const prefix = opts.symbol === false ? '' : currency === 'THB' ? '฿' : `${currency} `;
  return `${negative ? '-' : ''}${prefix}${body}`;
}

export function sumMinor(values: readonly number[]): number {
  let total = 0;
  for (const v of values) {
    if (!Number.isSafeInteger(v)) throw new MoneyParseError('ยอดเงินต้องเป็นจำนวนเต็มสตางค์');
    total += v;
  }
  return total;
}
